import { useMemo } from 'react'
import { createApiClient, type ApiClient } from '@behuman.corp/contracts'
import { useMedplum } from '@medplum/react'
import { config } from '@/config'
import { recoverAccessToken } from '@/auth/brokerSession'

// The contracts client throws the response BODY on failure (client.ts: `throw err.data`),
// so a middleware 401 surfaces as Nest's `{ statusCode: 401, ... }` payload.
function isUnauthorized(err: unknown): boolean {
  return (err as { statusCode?: number } | undefined)?.statusCode === 401
}

/**
 * 401-retry-once (PD-1512): the token getter reads medplum.getAccessToken() fresh per
 * request, so after a successful recovery the plain re-call carries the new token.
 * Wrapped here rather than in @behuman.corp/contracts — client.ts is do-not-modify.
 */
export function withAuthRetry(
  client: ApiClient,
  recover: () => Promise<string | undefined>,
): ApiClient {
  const wrap = <A extends unknown[], R>(fn: (...args: A) => Promise<R>) => {
    return async (...args: A): Promise<R> => {
      try {
        return await fn(...args)
      } catch (err) {
        if (!isUnauthorized(err)) throw err
        const token = await recover()
        if (!token) throw err
        return fn(...args)
      }
    }
  }

  return {
    ...client,
    get: wrap((...args) => client.get(...args)) as ApiClient['get'],
    post: wrap((...args) => client.post(...args)) as ApiClient['post'],
    put: wrap((...args) => client.put(...args)) as ApiClient['put'],
    patch: wrap((...args) => client.patch(...args)) as ApiClient['patch'],
    delete: wrap((...args) => client.delete(...args)) as ApiClient['delete'],
  }
}

export function useMiddlewareClient() {
  const medplum = useMedplum()

  // Keep the client stable.
  // A new client on every render changes effect dependencies and can cause fetch loops.
  return useMemo(
    () =>
      withAuthRetry(
        createApiClient({
          baseUrl: config.middlewareUrl || '-',
          tokenStorage: medplum,
        }),
        () => recoverAccessToken(medplum),
      ),
    [medplum],
  )
}
